import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Award, Coffee, TrendingUp, ShoppingBag, Users, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUser } from '@core/contexts/ProfileContext';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { gameApi } from '@core/game/api';
import { FloatingCoins, useFloatingCoins } from '@core/game/components/FloatingCoins';
import { ParticleEffect, useParticles } from '@core/game/components/ParticleEffect';
import { RippleEffect } from '@core/game/components/RippleEffect';
import { BuildingProgressBar } from '@core/game/components/BuildingProgressBar';
import { BuildingCard } from '@core/game/components/BuildingCard';
import { useGameProfile } from '@core/game/useGameProfile';

const BUILDING_DETAILS: Record<
  string,
  {
    description: string;
    icon: string;
  }
> = {
  coffee_stand: {
    description: 'Start small with a neighbourhood stand that brews classics.',
    icon: '☕'
  },
  coffee_shop: {
    description: 'Manage a cosy shop attracting loyal regulars all day.',
    icon: '🏬'
  },
  coffee_house: {
    description: 'Grow into a bustling coffee house with premium blends.',
    icon: '🏠'
  },
  coffee_factory: {
    description: 'Industrial roasting line for serious coin production.',
    icon: '🏭'
  }
};

interface Ripple {
  id: string;
  x: number;
  y: number;
}

interface BuildingUiModel {
  type: string;
  name: string;
  quantity: number;
  productionPerUnit: string;
  totalProduction: string;
  costDisplay: string;
  costValue: number;
  canAfford: boolean;
  description?: string;
  icon?: string;
}

export default function GamePage() {
  const { user, loading: userLoading } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [coins, setCoins] = useState<bigint>(BigInt(0));
  const [production, setProduction] = useState<bigint>(BigInt(0));
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const { clicks, addFloatingCoin } = useFloatingCoins();
  const { particleEffects, triggerParticles } = useParticles();
  const [isPulsing, setIsPulsing] = useState(false);

  const { data: gameData, isLoading: profileLoading } = useGameProfile(user?.id);

  const clickMutation = useMutation({
    mutationFn: () => gameApi.click(user!.id),
    onSuccess: (data) => {
      setCoins((prev) => prev + BigInt(data.earned));
      void queryClient.invalidateQueries({ queryKey: ['gameProfile'] });
    }
  });
  const buyBuildingMutation = useMutation({
    mutationFn: ({ buildingType, quantity }: { buildingType: string; quantity: number }) =>
      gameApi.buyBuilding(user!.id, buildingType, quantity),
    onSuccess: () => {
      toast.success('Building purchased!');
      void queryClient.invalidateQueries({ queryKey: ['gameProfile'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Purchase failed');
    }
  });

  const buyUpgradeMutation = useMutation({
    mutationFn: (upgradeId: string) => gameApi.buyUpgrade(user!.id, upgradeId),
    onSuccess: () => {
      toast.success('Upgrade purchased!');
      void queryClient.invalidateQueries({ queryKey: ['gameProfile'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Upgrade failed');
    }
  });

  useEffect(() => {
    if (gameData) {
      setCoins(BigInt(gameData.profile.coins));
      setProduction(BigInt(gameData.production));
    }
  }, [gameData]);

  useEffect(() => {
    if (production <= BigInt(0)) {
      return;
    }

    const interval = window.setInterval(() => {
      setCoins((prev) => prev + production);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [production]);

  const coinsPerClick = Number(gameData?.profile.level ?? 1);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!user || clickMutation.isPending) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      clickMutation.mutate();

      setIsPulsing(true);
      window.setTimeout(() => setIsPulsing(false), 200);

      addFloatingCoin(coinsPerClick, x, y);
      triggerParticles(event.clientX, event.clientY);

      const id = `${Date.now()}-${Math.random()}`;
      const rippleX = x;
      const rippleY = y;
      setRipples((prev) => [...prev, { id, x: rippleX, y: rippleY }]);

      window.setTimeout(() => {
        setRipples((prev) => prev.filter((ripple) => ripple.id !== id));
      }, 550);
    },
    [addFloatingCoin, clickMutation, coinsPerClick, triggerParticles, user]
  );

  const formatNumber = useCallback((value: bigint): string => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return value.toString();
    }
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toString();
  }, []);

  const buildings = useMemo<BuildingUiModel[]>(() => {
    if (!gameData) {
      return [];
    }

    return Object.entries(gameData.config.buildings).map(([type, config]) => {
      const building = gameData.profile.buildings.find((item) => item.buildingType === type);
      const quantity = building?.quantity ?? 0;
      const costMultiplier = Math.pow(config.costMultiplier, quantity);
      const costValue = Math.floor(config.baseCost * costMultiplier);
      const cost = BigInt(costValue);
      const productionTotal = BigInt(config.baseProduction) * BigInt(quantity);
      const details = BUILDING_DETAILS[type] ?? { description: '', icon: '🏢' };

      return {
        type,
        name: config.name,
        quantity,
        productionPerUnit: formatNumber(BigInt(config.baseProduction)),
        totalProduction: formatNumber(productionTotal),
        costDisplay: formatNumber(cost),
        costValue,
        canAfford: coins >= cost,
        description: details.description,
        icon: details.icon
      };
    });
  }, [coins, formatNumber, gameData]);

  if (userLoading || profileLoading) {
    return <div className="flex h-screen items-center justify-center">Loading game...</div>;
  }

  if (!user || !gameData) {
    return <div className="flex h-screen items-center justify-center">Please login to play</div>;
  }

  const coinsNumber =
    coins > BigInt(Number.MAX_SAFE_INTEGER) ? Number.MAX_SAFE_INTEGER : Number(coins);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 p-4 pb-28 text-white">
      {particleEffects.map((effect) => (
        <ParticleEffect key={effect.id} active x={effect.x} y={effect.y} />
      ))}
      <motion.div
        className="mb-6 grid grid-cols-2 gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 text-center text-white">
          <div className="text-3xl font-bold text-amber-200">
            <Coffee className="mr-2 inline-block" />
          </div>
          <div className="text-3xl font-bold text-amber-50">{formatNumber(coins)}</div>
          <div className="text-xs uppercase text-amber-200/80">Coins</div>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/15 to-teal-500/10 text-center text-white">
          <div className="text-2xl font-bold text-emerald-200">
            <TrendingUp className="mr-2 inline-block" />
            {formatNumber(production)}/s
          </div>
          <div className="text-xs uppercase text-emerald-200/80">Production</div>
        </Card>
      </motion.div>

      <Card className="relative mb-8 overflow-hidden border-white/10 bg-white/5">
        <motion.div
          className="relative flex h-64 cursor-pointer select-none items-center justify-center"
          onClick={handleClick}
          animate={
            isPulsing
              ? {
                  scale: [1, 1.05, 1],
                  boxShadow: [
                    '0 20px 60px rgba(251, 191, 36, 0.25)',
                    '0 25px 90px rgba(251, 191, 36, 0.45)',
                    '0 20px 60px rgba(251, 191, 36, 0.25)'
                  ]
                }
              : { scale: 1, boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)' }
          }
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-yellow-400/15 via-transparent to-transparent"
            animate={isPulsing ? { opacity: [0, 1, 0] } : { opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
          <motion.div
            whileTap={{ scale: 0.95 }}
            animate={isPulsing ? { rotate: [0, -2, 2, 0] } : { rotate: 0 }}
            transition={{ duration: 0.25 }}
            className="text-9xl drop-shadow-[0_12px_30px_rgba(249,168,37,0.45)]"
          >
            ☕
          </motion.div>

          <FloatingCoins clicks={clicks} />

          {ripples.map((ripple) => (
            <RippleEffect key={ripple.id} x={ripple.x} y={ripple.y} />
          ))}

          <motion.div
            className="absolute bottom-5 text-sm font-semibold text-amber-100"
            animate={{ scale: [1, 1.05, 1], opacity: [0.75, 1, 0.75] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            Tap to brew! ☕
          </motion.div>

          <motion.div
            className="absolute right-4 top-4 rounded-full bg-amber-300 px-3 py-1 text-sm font-bold text-amber-900 shadow-lg"
            animate={isPulsing ? { scale: [1, 1.15, 1] } : { scale: 1 }}
          >
            +{coinsPerClick} 🪙
          </motion.div>
        </motion.div>
      </Card>

      <section className="mb-8">
        <h2 className="mb-4 flex items-center text-xl font-bold text-white">
          <ShoppingBag className="mr-2" /> Buildings
        </h2>
        <div className="space-y-4">
          {buildings.map((building, index) => {
            const showProgress = !building.canAfford;

            return (
              <motion.div
                key={building.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
              >
                <BuildingCard
                  title={building.name}
                  description={building.description}
                  owned={building.quantity}
                  productionPerUnit={building.productionPerUnit}
                  totalProduction={building.totalProduction}
                  price={building.costDisplay}
                  disabled={!building.canAfford || buyBuildingMutation.isPending}
                  onPurchase={() =>
                    buyBuildingMutation.mutate({ buildingType: building.type, quantity: 1 })
                  }
                  progressBar={
                    showProgress ? (
                      <BuildingProgressBar
                        current={coinsNumber}
                        target={building.costValue}
                        label="Coins needed"
                      />
                    ) : undefined
                  }
                  icon={building.icon}
                />
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="mb-6">
        <h2 className="mb-4 flex items-center text-xl font-bold">
          <Zap className="mr-2" /> Upgrades
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(gameData.config.upgrades).map(([id, upgrade]) => {
            const purchased = Array.isArray((gameData.profile.gameState as any)?.upgrades)
              ? (gameData.profile.gameState as any).upgrades.includes(id)
              : false;
            const cost = BigInt(upgrade.cost);
            const canAfford = coins >= cost;

            return (
              <Card key={id} className={`text-center ${purchased ? 'opacity-60' : ''}`}>
                <div className="text-lg font-semibold capitalize">{id.replace(/_/g, ' ')}</div>
                <div className="mb-2 text-sm text-gray-600">x{upgrade.multiplier} production</div>
                {!purchased ? (
                  <Button
                    onClick={() => buyUpgradeMutation.mutate(id)}
                    disabled={!canAfford || buyUpgradeMutation.isPending}
                    size="sm"
                    variant={canAfford ? 'primary' : 'secondary'}
                  >
                    {formatNumber(cost)}
                  </Button>
                ) : (
                  <div className="text-sm font-semibold text-green-600">Purchased</div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      <div className="fixed bottom-4 left-0 right-0 mx-auto w-full max-w-md px-4">
        <div className="grid grid-cols-3 gap-2 rounded-xl bg-white/90 p-3 shadow-lg backdrop-blur">
          <Button variant="outline" size="sm" onClick={() => navigate('/game/achievements')}>
            <Award className="mr-1" size={16} />
            Achievements
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/game/leaderboard')}>
            <Users className="mr-1" size={16} />
            Leaderboard
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/game/shop')}>
            <Zap className="mr-1" size={16} />
            Energy Shop
          </Button>
        </div>
      </div>
    </div>
  );
}

